import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addTask } from '../redux/taskSlice';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import Logo from '../components/Logo';

const WRITING_STYLES = [
  'Journalistic Writing',
  'Documentary/Long-Form Narrative Writing',
  'SEO-Optimised Copywriting',
  'Thought Leadership & Opinion Writing',
  'Educational & Explanatory Writing',
  'Philosophical or Reflective Writing',
  'Creative Nonfiction',
  'Marketing and Sales Copywriting',
  'Technical Writing',
  'Research Papers & Academic Writing',
  'Case Studies',
  'Storytelling & Brand Journalism',
];

const CONTENT_FORMATS = [
  'Books', 'White Papers', 'Case Studies', 'Articles', 'Blog Posts', 'eBooks',
  'Research Reports', 'Marketing & Sales Copy', 'Technical Manuals & Documentation',
  'Speeches & Presentations', 'Press Releases', 'Web Content & Landing Pages',
  'Proposals & Grants', 'Thought Leadership Essays',
];

const USER_MODES = [
  { value: 'FULL_AUTOMATION', label: 'Full Automation', desc: 'AI handles everything automatically' },
  { value: 'ASSISTED', label: 'Assisted', desc: 'AI runs each step, you review before continuing' },
  { value: 'MANUAL', label: 'Manual', desc: 'You control when each step starts' },
];

const SCRIBE_GREETING = `I am **The Scribe**.
I prepare the brief for your writing team.
What are you creating today?`;

function formatMessage(text) {
  // Simple markdown-lite: bold, newlines
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

export default function TaskCreation() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: SCRIBE_GREETING },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [brief, setBrief] = useState(null);

  // Launch panel state — pre-filled from brief, user can adjust
  const [userMode, setUserMode] = useState('FULL_AUTOMATION');
  const [contentFormat, setContentFormat] = useState('');
  const [writingStyle, setWritingStyle] = useState('');
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState('');
  const [limitReached, setLimitReached] = useState(null); // {tier, limit}

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, isComplete]);

  // When brief arrives, pre-fill launch panel
  useEffect(() => {
    if (brief) {
      setUserMode(brief.user_mode || 'FULL_AUTOMATION');
      setContentFormat(brief.content_format || '');
      setWritingStyle(brief.writing_style || '');
    }
  }, [brief]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading || isComplete) return;

    const newUserMsg = { role: 'user', content: text };
    const updatedMessages = [...messages, newUserMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const history = updatedMessages.slice(0, -1).map(m => ({
        role: m.role,
        content: m.content,
      }));

      const res = await apiClient.post('/intake-valet/', {
        messages: history,
        message: text,
      });

      const { reply, is_complete, brief: receivedBrief } = res.data;
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);

      if (is_complete && receivedBrief) {
        setIsComplete(true);
        setBrief(receivedBrief);
      }
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Sorry, I had a technical issue. Please try again." },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleLaunch = async () => {
    if (!contentFormat || !writingStyle || !brief) return;
    setLaunchError('');
    setLimitReached(null);
    setLaunching(true);

    const scribeConversation = messages
      .filter(msg => msg?.role === 'user' || msg?.role === 'assistant')
      .map(msg => ({ role: msg.role, content: msg.content }));

    const userHeart = scribeConversation
      .filter(msg => msg.role === 'user')
      .map(msg => msg.content)
      .join('\n\n');

    const payload = {
      document_type: contentFormat,
      content_format: contentFormat,
      writing_style: writingStyle,
      user_mode: userMode,
      topic: brief.topic,
      user_request: userHeart || brief.topic,
      audience: brief.audience || '',
      intake_brief: brief,
      scribe_conversation: scribeConversation,
      scribe_user_heart: userHeart,
      confirmed: true,
    };

    try {
      const response = await apiClient.post('/user-request/', payload);
      dispatch(addTask(payload));
      navigate('/studio', {
        state: { workflowInstanceId: response.data.workflow_instance_id },
      });
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 429 || data?.limit_reached) {
        setLimitReached({ tier: data.tier, limit: data.workflow_limit });
      } else {
        setLaunchError(data?.error || 'Launch failed. Please try again.');
      }
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#0A0F1E',
      fontFamily: 'Inter, sans-serif',
      color: '#E2E8F0',
      overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{
        flexShrink: 0,
        padding: isMobile ? '14px 16px 12px' : '18px 32px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        background: 'rgba(10,15,30,0.95)',
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <Logo size={36} style={{ flexShrink: 0 }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#F1F5F9' }}>The Scribe</div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Preparing your brief for the writing team</div>
        </div>
        <div style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: '#22C55E',
        }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#22C55E' }} />
          Online
        </div>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: isMobile ? '20px 12px 20px' : '28px 24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxWidth: 760,
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            gap: 10,
            alignItems: 'flex-start',
          }}>
            {msg.role === 'assistant' && (
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #1E3A8A, #F97316)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, color: 'white', marginTop: 2,
              }}>S</div>
            )}
            <div style={{
              maxWidth: isMobile ? '82%' : '72%',
              padding: '12px 16px',
              borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
              background: msg.role === 'user'
                ? 'linear-gradient(135deg, #1E3A8A, #1D4ED8)'
                : 'rgba(255,255,255,0.06)',
              border: msg.role === 'assistant' ? '1px solid rgba(255,255,255,0.08)' : 'none',
              fontSize: 14,
              lineHeight: 1.65,
              color: '#E2E8F0',
            }}
              dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
            />
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #1E3A8A, #F97316)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: 'white',
            }}>S</div>
            <div style={{
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '4px 18px 18px 18px',
              display: 'flex', gap: 5, alignItems: 'center',
            }}>
              {[0, 1, 2].map(d => (
                <div key={d} style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: '#64748B',
                  animation: `pulse 1.2s ease-in-out ${d * 0.2}s infinite`,
                }} />
              ))}
            </div>
          </div>
        )}

        {/* Launch Panel — appears when brief is complete */}
        {isComplete && brief && (
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(249,115,22,0.3)',
            borderRadius: 16,
            padding: isMobile ? 16 : 24,
            marginTop: 8,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: '#F97316',
              }} />
              <div style={{ fontWeight: 700, fontSize: 15, color: '#F97316' }}>
                Brief Complete — Review & Launch
              </div>
            </div>

            {/* Brief summary */}
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 10,
              padding: '12px 14px',
              marginBottom: 20,
              fontSize: 13,
              lineHeight: 1.7,
              color: '#CBD5E1',
              borderLeft: '3px solid rgba(30,58,138,0.6)',
            }}>
              <div><span style={{ color: '#94A3B8' }}>Topic:</span> {brief.topic}</div>
              <div><span style={{ color: '#94A3B8' }}>Audience:</span> {brief.audience}</div>
              {brief.key_messages?.length > 0 && (
                <div><span style={{ color: '#94A3B8' }}>Key messages:</span> {brief.key_messages.join(' · ')}</div>
              )}
              {brief.constraints && (
                <div><span style={{ color: '#94A3B8' }}>Constraints:</span> {brief.constraints}</div>
              )}
            </div>

            {/* Adjustable fields */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', marginBottom: 6 }}>
                  Content Format
                </div>
                <select
                  value={contentFormat}
                  onChange={e => setContentFormat(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8, color: '#E2E8F0', fontSize: 13,
                    outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="">Select format</option>
                  {CONTENT_FORMATS.map(f => (
                    <option key={f} value={f} style={{ background: '#1E293B' }}>{f}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', marginBottom: 6 }}>
                  Writing Style
                </div>
                <select
                  value={writingStyle}
                  onChange={e => setWritingStyle(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8, color: '#E2E8F0', fontSize: 13,
                    outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="">Select style</option>
                  {WRITING_STYLES.map(s => (
                    <option key={s} value={s} style={{ background: '#1E293B' }}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mode selector */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', marginBottom: 8 }}>
                Automation Mode
              </div>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
                {USER_MODES.map(({ value, label, desc }) => {
                  const sel = userMode === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setUserMode(value)}
                      style={{
                        flex: 1, padding: '10px 12px', textAlign: 'left',
                        borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                        border: sel ? '1.5px solid #F97316' : '1px solid rgba(255,255,255,0.1)',
                        background: sel ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.03)',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 12, color: sel ? '#F97316' : '#CBD5E1', marginBottom: 2 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748B', lineHeight: 1.4 }}>{desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {limitReached && (
              <div style={{
                background: 'rgba(249,115,22,0.08)',
                border: '1px solid rgba(249,115,22,0.35)',
                borderRadius: 10, padding: '14px 16px', marginBottom: 14,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#F97316', marginBottom: 4 }}>
                  ⚠ Monthly limit reached
                </div>
                <div style={{ fontSize: 13, color: '#CBD5E1', marginBottom: 12 }}>
                  You've used all {limitReached.limit} workflows this month on your{' '}
                  <strong>{limitReached.tier}</strong> plan.
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  style={{
                    padding: '8px 18px', background: '#F97316', color: 'white',
                    border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  Upgrade Plan →
                </button>
              </div>
            )}

            {launchError && (
              <div style={{
                background: 'rgba(220,38,38,0.1)',
                border: '1px solid rgba(220,38,38,0.3)',
                color: '#FCA5A5', borderRadius: 8,
                padding: '10px 14px', fontSize: 13, marginBottom: 14,
              }}>
                {launchError}
              </div>
            )}

            <button
              onClick={handleLaunch}
              disabled={!contentFormat || !writingStyle || launching || !!limitReached}
              style={{
                width: '100%', height: 52,
                background: contentFormat && writingStyle && !launching
                  ? '#F97316'
                  : 'rgba(255,255,255,0.08)',
                color: contentFormat && writingStyle ? 'white' : '#64748B',
                border: 'none', borderRadius: 10,
                fontSize: 16, fontWeight: 700,
                fontFamily: 'Syne, sans-serif',
                cursor: contentFormat && writingStyle && !launching ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s',
                letterSpacing: '0.01em',
              }}
              onMouseEnter={e => { if (contentFormat && writingStyle && !launching) e.target.style.background = '#EA580C'; }}
              onMouseLeave={e => { if (contentFormat && writingStyle && !launching) e.target.style.background = '#F97316'; }}
            >
              {launching ? 'Launching Writing Team...' : 'Launch Writing Team →'}
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input bar */}
      {!isComplete && (
        <div style={{
          flexShrink: 0,
          padding: '12px 16px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(10,15,30,0.95)',
          backdropFilter: 'blur(12px)',
        }}>
          <div style={{
            maxWidth: 760,
            margin: '0 auto',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              placeholder="Type your message... (Enter to send)"
              rows={1}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                color: '#E2E8F0',
                fontSize: 14,
                fontFamily: 'Inter, sans-serif',
                outline: 'none',
                resize: 'none',
                lineHeight: 1.5,
                maxHeight: 120,
                overflowY: 'auto',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => { e.target.style.borderColor = 'rgba(30,58,138,0.6)'; }}
              onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              style={{
                width: 44, height: 44, flexShrink: 0,
                background: input.trim() && !loading
                  ? '#F97316'
                  : 'rgba(255,255,255,0.06)',
                border: 'none', borderRadius: 10,
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
                fontSize: 18, color: input.trim() && !loading ? 'white' : '#475569',
              }}
            >
              ↑
            </button>
          </div>
          <div style={{
            maxWidth: 760, margin: '8px auto 0',
            fontSize: 11, color: '#334155', textAlign: 'center',
          }}>
            Enter to send · Shift+Enter for new line
          </div>
        </div>
      )}



      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        select option { background: #1E293B; color: #E2E8F0; }
      `}</style>
    </div>
  );
}
